import React from 'react';
import moment from 'moment';
import { Label } from 'semantic-ui-react';

import PediatricIndicatorIcon from '../../../../components/Icons/PediatricIndicatorIcon';
import SummaryPopover, {
  popoverCellStyle,
} from '../../../../components/SummaryPopover';
import {
  REVIEW_TYPES_REQUESTED,
  ROLES,
  XSOLIS_USER_TYPE,
} from '../../../../libs/Constants';
import { formatDate } from '../../../../libs/reactTableFormatters';
import Assignment from '../../../Assignment';
import { assignmentAccessor } from '../../accessors/assignmentAccessor';
import {
  dateOfBirthValueRenderer,
  MaxClsValueRenderer,
  MeLValueRenderer,
  PediatricIndicatorRenderer,
  TransferIndicatorRenderer,
} from '../../CellRendererComponents';
import { FormatLastPADate, Time } from '../../components/Time';
import { MEL_OPTIONS } from '../../constants/MeLValues.constants';
import { PEDIATRIC_INDICATOR_OPTIONS } from '../../constants/PediatricIndicatorValues.constants';
import {
  DEFAULT_TIME_FORMAT,
  GMT_TIME_FORMAT,
} from '../../constants/TimeCalculation.constants';
import { filterByDate, MeLFilterMethod } from '../../customFilters';
import { SelectColumnFilter } from '../../customFilters/filters/SelectFilter';
import QueueStatusIndicatorControl from '../../QueueStatusIndicator';
import ReviewType from '../../ReviewType';
import {
  getPediatricIndicator,
  sortByAssignment,
  sortByMeL,
  sortWithNullFirst,
} from '../index';
import { sortWithBooleanValues } from '../sortForBooleanValues';
import { getTotalLosColumnDef } from './getTotalLosColumnDef';

export const getClientColumns = function (props) {
  /*
    Make sure to add hidden columns to the end of the list to not mess
    up the rearrangement logic.  Sorry for the fragility :(
  */

  const defaults = [
    {
      accessor: (d) => d.is_pending,
      Cell: ({ row }) => {
        return <QueueStatusIndicatorControl row={row} />;
      },
      filterable: false,
      Header: 'PA Status',
      id: 'is_pending',
      width: 100,
    },
    {
      accessor: 'high_priority_yn',
      Cell: ({ row }) => {
        if (row.high_priority_yn) {
          return (
            <Label
              color='red'
              data-admission-id={row.admissionid}
              data-entity-id={row.entityid}
              data-is-high-priority={true}
              style={{ fontSize: '10pt', textAlign: 'Center', width: '100%' }}
            >
              !
            </Label>
          );
        } else {
          return <span></span>;
        }
      },
      filterable: false,
      Header: 'Priority',
      id: 'high_priority_yn',
      width: 100,
    },
    {
      accessor: (d) => {
        return assignmentAccessor(
          d.assigned_username,
          props.reviewers,
          d.admission_id,
          d.entity_id
        );
      },
      Cell: ({ row }) => {
        return (
          <Assignment
            admissionId={row.admissionid}
            assignedUser={row.currentAssignment}
            assignmentCompleted={props.handleAssignmentComplete}
            entityId={row.entityid}
            fetchQueue={props.fetchQueue}
            hasOpenPaReview={row.has_open_pa_review}
            includesEventQueue={props.includesEventQueue}
            isExternalAdmin={
              props.userRoles?.find(
                (r) => r.xpas_role_id === ROLES.ADMINISTRATOR
              ) && props.userTypeId !== XSOLIS_USER_TYPE
            }
            isInternalSysAdmin={
              props.userRoles?.find(
                (r) => r.xpas_role_id === ROLES.SYSTEM_ADMINISTRATOR
              ) && props.userTypeId === XSOLIS_USER_TYPE
            }
            isManualReviewType={
              row.review_type_requested === REVIEW_TYPES_REQUESTED.MANUAL
            }
            isPAUser={props.userRoles?.find(
              (r) => r.xpas_role_id === ROLES.PHYSICIAN_ADVISOR
            )}
            isTeamLead={props.userRoles?.find(
              (r) => r.xpas_role_id === ROLES.TEAM_LEAD
            )}
            patientId={row.patientid}
            patientName={row.patientName}
            queueState={props.state}
            reviewers={props.reviewers}
            setQueueState={props.setState}
            style={{ maxWidth: '200px' }}
            username={props.username}
          />
        );
      },
      filterable: false,
      Header: 'Assignment',
      id: 'assignedUsername',
      resizable: false,
      sortMethod: sortByAssignment,
      style: { overflow: 'visible' },
      width: 235,
    },
    {
      accessor: (d) => d.patient_name,
      Cell: ({ row }) => {
        return (
          <span
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'left',
            }}
          >
            <div style={popoverCellStyle}>
              <SummaryPopover
                goToMessageFeed={props.goToMessageFeed}
                handleClose={props.handleClose}
                handleOpen={props.handleOpen}
                handleReviewsButton={props.handleReviewsButton}
                patientName={row.patientName}
                row={row}
              />
            </div>
          </span>
        );
      },
      Header: 'Patient',
      id: 'patientName',
      width: 200,
    },
    {
      accessor: (d) => {
        return getPediatricIndicator(d.admission_date, d.birth_date);
      },
      Cell: PediatricIndicatorRenderer,
      Filter: ({ filter, onChange }) => {
        return SelectColumnFilter(
          filter,
          PEDIATRIC_INDICATOR_OPTIONS,
          onChange
        );
      },
      filterable: true,
      Header: <PediatricIndicatorIcon />,
      id: 'pediatricIndicator',
      sortable: true,
      sortMethod: sortWithBooleanValues,
      width: 85,
    },
    {
      accessor: 'birth_date',
      Cell: dateOfBirthValueRenderer,
      filterMethod: filterByDate,
      Header: 'DOB',
      id: 'birthDate',
      width: 120,
    },
    {
      accessor: (d) => d.visit_nbr,
      Header: 'Account #',
      id: 'accountNumber',
      width: 140,
    },
    {
      accessor: (d) => d.entity_name,
      Cell: TransferIndicatorRenderer,
      filterMethod: (filter, row) => {
        const id = filter.pivotId || filter.id;
        return row[id] !== undefined && row[id] !== null
          ? String(String(row[id]).toLowerCase()).includes(
              filter.value.toLowerCase()
            )
          : false;
      },
      Header: 'Facility',
      id: 'facility',
      width: 275,
    },
    {
      accessor: (l) => l.point_of_care_description,
      Header: 'Location',
      id: 'point_of_care_description',
      width: 200,
    },
    {
      accessor: 'room_nbr',
      Header: 'Room #',
      id: 'room_nbr',
      width: 150,
    },
    {
      accessor: 'admission_date',
      Cell: (row) => <Time value={row.value} />,
      filterable: false,
      Header: 'Presentation Date',
      id: 'admission_date',
      width: 150,
    },
    {
      accessor: 'client_escalation_date',
      Cell: ({ row }) => {
        if (!row.client_escalation_date) {
          return <span></span>;
        } else {
          return <Time value={row.client_escalation_date} />;
        }
      },
      filterable: false,
      Header: 'Escalation Date',
      id: 'client_escalation_date',
      width: 150,
    },
    {
      accessor: (d) =>
        d.escalating_review_type_desc === 'Appeal/Retrospective'
          ? 'Appeal/Retrospective'
          : d.review_type_requested === 'Physician Advisor'
          ? 'Status Determination'
          : d.review_type_requested,
      Cell: ({ original, row }) => {
        if (original && original.manual_escalation) {
          return (
            <ReviewType
              data={original}
              setState={props.setState}
              state={props.state}
              userTypeId={props.userTypeId}
            />
          );
        } else {
          return row.review_type_requested;
        }
      },
      filterable: false,
      Header: 'Review Type',
      id: 'review_type_requested',
      resizable: false,
      style: { overflow: 'visible' },
      width: 235,
    },
    {
      accessor: (d) => d.scheduled_date_time,
      Cell: ({ row }) => {
        //decode utc and facility time zone before displaying on PA work queue for DF only
        if (
          /[-]/.test(row.admission_id) &&
          row.scheduled_date_time &&
          row.facilitytimezone
        ) {
          const gmtDateTime = moment.utc(
            row.scheduled_date_time,
            GMT_TIME_FORMAT
          );
          const localDateTime = gmtDateTime
            .local()
            .tz(row.facilitytimezone)
            .format(DEFAULT_TIME_FORMAT);

          return localDateTime;
        } else {
          return <Time value={row.scheduled_date_time} />;
        }
      },
      Filter: ({ filter, onChange }) => (
        <input
          className='grid-filter-p2p-scheduled-datetime'
          onChange={(event) => onChange(event.target.value)}
          style={{
            width: '100%',
          }}
          type='text'
          value={filter ? filter.value : ''}
        />
      ),
      Header: 'P2P Scheduled Date/Time',
      id: 'scheduled_date_time',
      sortable: true,
      width: 255,
    },
    {
      accessor: 'review_due_date',
      Cell: formatDate,
      filterable: false,
      Header: 'Review Due',
      id: 'review_due_date',
      width: 130,
    },
    {
      accessor: (d) => d.second_level_reason_text,
      Header: '2nd Level Reason',
      id: 'second_level_reason_text',
      width: 160,
    },
    {
      accessor: 'attending_physician',
      Header: 'Attending Physician',
      id: 'attending_physician',
      width: 150,
    },
    {
      accessor: 'last_pa_review_user',
      Cell: (row) => (
        <span>
          {row.original.last_pa_review_user}{' '}
          <FormatLastPADate value={row.original.last_pa_review_date} />
        </span>
      ),
      filterable: false,
      Header: 'Last PA Review',
      id: 'last_pa_review_user',
      width: 300,
    },
    {
      accessor: (d) => d.financial_class,
      Header: 'Payer-Primary',
      id: 'primaryPayer',
      width: 240,
    },
    {
      accessor: (d) => d.secondary_financial_class,
      Header: 'Secondary Payer',
      id: 'secondaryPayer',
      width: 240,
    },
    {
      accessor: (d) => d.admission_status,
      Header: 'Status',
      id: 'status',
      width: 150,
    },
    {
      accessor: 'auto_escalation_reason_description',
      filterable: true,
      Header: 'Auto-Escalation Reason',
      id: 'auto_escalation_reason_description',
      width: 180,
    },
    {
      accessor: (d) => d.admission_id,
      Filter: ({ filter, onChange }) => (
        <input
          className='grid-filter-admission-id'
          onChange={(event) => onChange(event.target.value)}
          style={{
            width: '100%',
          }}
          type='text'
          value={filter ? filter.value : ''}
        />
      ),
      Header: 'Admission Id',
      id: 'admission_id',
      width: 125,
    },
    {
      accessor: 'mrn',
      filterable: true,
      Header: 'MRN',
      id: 'mrn',
      width: 125,
    },
    {
      accessor: 'chief_complaint',
      Header: 'Chief Complaint',
      id: 'chiefComplaint',
      width: 180,
    },
    {
      accessor: 'current_cls',
      filterable: false,
      Header: 'Current CLS',
      id: 'current_cls',
      sortable: true,
      sortMethod: sortWithNullFirst,
      width: 130,
    },
    {
      accessor: 'max_cls',
      Cell: MaxClsValueRenderer,
      filterable: false,
      Header: 'Max CLS',
      id: 'max_cls',
      sortMethod: sortWithNullFirst,
      width: 100,
    },
    getTotalLosColumnDef(props.enableLOSDecimal),
    {
      accessor: 'mel',
      Cell: MeLValueRenderer,
      Filter: ({ filter, onChange }) => {
        return SelectColumnFilter(filter, MEL_OPTIONS, onChange);
      },
      filterable: true,
      filterMethod: MeLFilterMethod,
      Header: 'MeL',
      id: 'mel',
      sortable: true,
      sortMethod: sortByMeL,
      width: 150,
    },
    {
      accessor: (d) => d.entity_id,
      id: 'entityid',
      show: false,
      width: 0,
    },
    {
      accessor: (d) => d.patient_id,
      id: 'patientid',
      show: false,
      width: 0,
    },
    {
      accessor: (d) => d.admission_id,
      id: 'admissionid',
      show: false,
      width: 0,
    },
    {
      accessor: (d) => d.target_encounter_id,
      id: 'target_encounter_id',
      show: false,
      width: 0,
    },
    {
      accessor: (d) => d.assigned_username,
      Header: 'Current Assignment',
      id: 'currentAssignment',
      show: false,
      width: 0,
    },
    {
      accessor: 'peer_to_peer_deadline_date',
      Header: 'P2P Review Due',
      id: 'peer_to_peer_deadline_date',
      show: false,
      width: 0,
    },
    {
      accessor: 'schedule_option_selected',
      id: 'schedule_option_selected',
      show: false,
      width: 0,
    },
    {
      accessor: 'has_open_pa_review',
      id: 'has_open_pa_review',
      show: false,
      width: 0,
    },
    {
      accessor: (d) => {
        const { facilitiesTimezones } = props.state;
        if (facilitiesTimezones) {
          const rowTimezoneObject = facilitiesTimezones.find(
            (facility) => facility.ENTITY_ID === d.entity_id
          );
          return rowTimezoneObject && rowTimezoneObject.TIMEZONE;
        }
      },
      id: 'facilitytimezone',
      show: false,
      width: 0,
    },
  ];

  return defaults;
};
